import { graphql } from '@api/highnote'
import { useQuery } from '@apollo/client'
import { HighnoteClient as client } from '@/src/services/Highnote'
import { useLogError } from '@/src/common/hooks/useLogError'
import type { GetPaymentCardQuery, GetPaymentCardQueryVariables } from '@api/highnote/graphql'
import { useCombinedCardStatusStore } from '@/src/experiences/virtual-card/store/useCombinedCardStatusStore'
import { useClientToken } from '@/src/app/main/(tabs)/card/card-management/virtual-card/useClientToken'

export function useLegacyPaymentCard({
  variables,
}: Pick<
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  NonNullable<Parameters<typeof useQuery<GetPaymentCardQuery, GetPaymentCardQueryVariables>>[1]>,
  'variables'
>) {
  const clientToken = useClientToken()
  const onError = useLogError()
  const { setStatus } = useCombinedCardStatusStore()

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { data } = useQuery(
    graphql(`
      query GetPaymentCard($paymentCardId: ID!) {
        node(id: $paymentCardId) {
          __typename
          ... on PaymentCard {
            id
            bin
            last4
            expirationDate
            status
            restrictedDetails {
              __typename
              ... on PaymentCardRestrictedDetails {
                number
                cvv
              }
            }
          }
        }
      }
    `),
    {
      client,
      onError,
      context: { clientToken },
      skip: !clientToken || !variables?.paymentCardId,
      variables,
      onCompleted(data) {
        if (
          data.node?.__typename === 'PaymentCard' &&
          data.node.status &&
          data.node.restrictedDetails?.__typename === 'PaymentCardRestrictedDetails'
        ) {
          setStatus(data.node.status)
          return
        }

        const error = new Error('Invalid response for PaymentCard & PaymentCardRestrictedDetails', {
          cause: data,
        })

        onError(error)
        setStatus({ error })
      },
    },
  )

  return data?.node?.__typename === 'PaymentCard' ? data.node : undefined
}

export type LegacyPaymentCard = ReturnType<typeof useLegacyPaymentCard>
